import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  fetchSubscription,
  planKeys,
  syncPaymentStatus,
  type PaymentKind,
  type PaymentStatus,
} from '@entities/Plan';
import { profileKeys } from '@entities/Profile';
import { trackGoal } from '@shared/analytics';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, Logo } from '@shared/ui';

type View = 'pending' | 'success' | 'canceled' | 'error';

const PLAN_LABEL: Record<string, string> = { DEMO: 'Demo', PRO: 'Pro', PREMIUM: 'Max' };

// Платёж capture=true обычно приходит «succeeded» уже к моменту возврата, но
// иногда YooKassa ещё досчитывает — поэтому несколько раз перепроверяем статус.
const MAX_ATTEMPTS = 4;
const RETRY_DELAY_MS = 1500;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Отметка «оплату уже засчитали в аналитике»: перезагрузка страницы, вкладка, которую
// восстановил браузер, или повторный заход по адресу с тем же pid не удваивают покупку.
// Для Метрики это единственная защита от повтора, для GA4 — пока тег GTM не передаёт
// transaction_id (шаг владельца в docs/project/operations.md).
const PAID_MARK_PREFIX = 'lx.analytics.paid.';

/** true — платёж засчитываем впервые на этом устройстве (и сразу ставим отметку). */
function markPaidOnce(pid: string): boolean {
  try {
    if (localStorage.getItem(PAID_MARK_PREFIX + pid)) return false;
    localStorage.setItem(PAID_MARK_PREFIX + pid, '1');
  } catch {
    // Без хранилища повтор отсечёт только insert_id в Amplitude. GA4 — когда тег GTM начнёт
    // передавать transaction_id (шаг владельца); Метрика повтор засчитает.
  }
  return true;
}

/** Строка чека: подпись слева, значение справа. */
function ReceiptRow({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-line px-4 py-3 last:border-b-0">
      <span className="text-[14px] text-muted">{label}</span>
      <span className={cn('text-[14px] font-medium', accent && 'text-accent')}>{value}</span>
    </div>
  );
}

export default function PaymentSuccessPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const pid = params.get('pid');

  const [view, setView] = useState<View>('pending');
  const [kind, setKind] = useState<PaymentKind>('SUBSCRIPTION');
  const [tier, setTier] = useState<string>('');
  const [amountRub, setAmountRub] = useState(0);
  const [planExpiresAt, setPlanExpiresAt] = useState<string | null>(null);
  const fired = useRef(false);

  const lang = (i18n.resolvedLanguage || i18n.language || 'ru').split('-')[0];
  const fmtDate = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'ru-RU', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }).format(new Date(iso))
      : '';

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    if (!pid) {
      setView('error');
      return;
    }

    (async () => {
      let lastStatus: PaymentStatus | null = null;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        try {
          const res = await syncPaymentStatus(pid);
          lastStatus = res.status;
          setKind(res.kind);
          setTier(res.tier ?? '');
          if (res.status === 'SUCCEEDED') {
            // Конверсия-оплата: привязка канала (UTM/referrer) к выручке. Один раз на платёж.
            if (markPaidOnce(pid)) {
              trackGoal(res.kind === 'DONATION' ? 'donation' : 'purchase', {
                tier: res.tier ?? '',
                amount: res.amountRub,
                transaction_id: pid,
              });
            }
            if (res.kind === 'DONATION') {
              // Донат проставил has_donated — освежаем профиль (плашка на главной).
              await qc.invalidateQueries({ queryKey: profileKeys.current });
            } else {
              setAmountRub(res.amountRub);
              // Дату окончания берём свежим запросом подписки (а не из мутируемого
              // в этом же запросе юзера) — это гарантированно актуальное значение
              // после активации. Заодно кладём свежую подписку в кэш для /plans.
              try {
                const sub = await qc.fetchQuery({
                  queryKey: planKeys.subscription,
                  queryFn: fetchSubscription,
                });
                setPlanExpiresAt(sub.planExpiresAt);
              } catch {
                // не критично — чек всё равно покажет тариф и сумму
              }
              await qc.invalidateQueries({ queryKey: planKeys.list });
              await qc.invalidateQueries({ queryKey: planKeys.quota });
              // Помесячная модель «Тарифы»: оплата меняла фронтир/историю/период —
              // инвалидируем overview, чтобы страница перерисовалась после возврата.
              await qc.invalidateQueries({ queryKey: planKeys.billing });
            }
            setView('success');
            return;
          }
          if (res.status === 'CANCELED') {
            setView('canceled');
            return;
          }
        } catch {
          // сетевая/провайдерская ошибка — повторим
        }
        if (attempt < MAX_ATTEMPTS - 1) await delay(RETRY_DELAY_MS);
      }
      // Терминального статуса не дождались. Деньги могли списаться — эффект
      // применит вебхук; показываем reassuring-ошибку.
      setView('error');
      void lastStatus;
    })();
  }, [pid, qc]);

  const goNext = () => navigate(kind === 'DONATION' ? paths.dashboard : paths.plans, { replace: true });

  const isDonation = kind === 'DONATION';
  const planLabel = PLAN_LABEL[tier] ?? tier;

  return (
    <div className="flex h-dvh w-full flex-col overflow-y-auto bg-base px-5 py-8 sm:px-8 md:px-12 md:py-10">
      <div className="mb-8 flex items-center">
        <Logo />
      </div>

      <div className="flex flex-1 items-center justify-center">
        {view === 'pending' && (
          <div className="flex max-w-[400px] flex-col items-center gap-[18px] text-center">
            <span
              aria-hidden
              className="block h-12 w-12 animate-spin rounded-full border-2 border-line border-t-accent"
            />
            <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[36px]">
              {t('payment.pending.title')}
            </h1>
            <div className="text-[14px] leading-[1.6] text-muted">{t('payment.pending.sub')}</div>
          </div>
        )}

        {view === 'success' && (
          <div className="flex w-full max-w-[420px] flex-col items-center gap-[18px] text-center">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-[20px] bg-accent/15 text-3xl text-accent">
              {isDonation ? '♥' : '✓'}
            </div>
            <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[36px]">
              {isDonation ? t('payment.donation.title') : t('payment.success.title')}
            </h1>
            <div className="text-[14px] leading-[1.6] text-muted">
              {isDonation ? t('payment.donation.sub') : t('payment.success.sub', { tier: planLabel })}
            </div>

            {!isDonation && (
              <>
                <div className="w-full overflow-hidden rounded-[16px] border border-line bg-card text-left">
                  <ReceiptRow label={t('payment.success.planRow')} value={planLabel} />
                  {amountRub > 0 && (
                    <ReceiptRow label={t('payment.success.amountRow')} value={`${amountRub} ₽`} />
                  )}
                  {planExpiresAt && (
                    <ReceiptRow label={t('payment.success.untilRow')} value={fmtDate(planExpiresAt)} accent />
                  )}
                </div>
                <div className="text-[12px] leading-[1.5] text-muted-2">{t('payment.success.note')}</div>
              </>
            )}

            <Button variant="primary" onClick={goNext} className="mt-1">
              {isDonation ? t('payment.donation.cta') : t('payment.success.cta')}
            </Button>
          </div>
        )}

        {view === 'canceled' && (
          <div className="flex max-w-[400px] flex-col items-center gap-[18px] text-center">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-[20px] bg-elev text-3xl text-muted">
              ✕
            </div>
            <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[36px]">
              {t('payment.canceled.title')}
            </h1>
            <div className="text-[14px] leading-[1.6] text-muted">{t('payment.canceled.sub')}</div>
            <Button variant="ghost" onClick={goNext} className="mt-1">
              {t('payment.canceled.cta')}
            </Button>
          </div>
        )}

        {view === 'error' && (
          <div className="flex max-w-[400px] flex-col items-center gap-[18px] text-center">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-[20px] bg-elev text-3xl text-muted">
              ⏳
            </div>
            <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[36px]">
              {t('payment.error.title')}
            </h1>
            <div className="text-[14px] leading-[1.6] text-muted">{t('payment.error.sub')}</div>
            <Button variant="ghost" onClick={goNext} className="mt-1">
              {t('payment.error.cta')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
